import React, { useState, useEffect } from 'react';
import { RefreshCw, X, Sparkles, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const PWAUpdatePrompt = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updateInfo, setUpdateInfo] = useState({ version: '', message: '' });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Listen for messages from service worker
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'APP_UPDATE_AVAILABLE') {
        console.log('📱 Update available:', event.data);
        setUpdateInfo({
          version: event.data.version,
          message: event.data.message
        });
        setShowUpdate(true);
        
        // If force update, auto-refresh after 5 seconds
        if (event.data.forceUpdate) {
          setTimeout(() => {
            handleUpdate();
          }, 5000);
        }
      }
      
      if (event.data && event.data.type === 'REFRESH_APP') {
        // Force refresh from service worker
        window.location.reload(true);
      }
    };

    // Register message listener
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Also check version on load
    checkVersion();
    
    // Check version every 60 seconds
    const interval = setInterval(checkVersion, 60000);

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
      clearInterval(interval);
    };
  }, []);

  const checkVersion = async () => {
    try {
      const storedVersion = localStorage.getItem('app_version');
      
      const response = await fetch(`${API}/app/version`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (!response.ok) return;
      
      const data = await response.json();
      
      if (data.success && data.force_update) {
        // Check if version changed
        if (storedVersion && storedVersion !== data.version) {
          setUpdateInfo({
            version: data.version,
            message: data.update_message || 'A new version is available!'
          });
          setShowUpdate(true);
        } else if (!storedVersion) {
          // First time - store version
          localStorage.setItem('app_version', data.version);
        }
      }
    } catch (error) {
      // Silently fail
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    try {
      // Store new version
      localStorage.setItem('app_version', updateInfo.version);
      
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Tell service worker to skip waiting
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
      }
      
      // Force reload
      setTimeout(() => {
        window.location.reload(true);
      }, 500);
    } catch (error) {
      console.error('Update failed:', error);
      // Force reload anyway
      window.location.reload(true);
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
    // Store version to prevent showing again
    localStorage.setItem('app_version', updateInfo.version);
  };

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[200] animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-2xl overflow-hidden border border-white/20">
        {/* Progress bar for auto-update */}
        {isUpdating && (
          <div className="h-1 bg-white/20">
            <div className="h-full bg-white animate-pulse w-full"></div>
          </div>
        )}
        
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white font-bold text-sm">
                  New Update Available! 🎉
                </h3>
                <button 
                  onClick={handleDismiss}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-white/70" />
                </button>
              </div>
              
              <p className="text-white/80 text-xs mb-3">
                {updateInfo.message}
              </p>
              
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  size="sm"
                  className="bg-white text-purple-600 hover:bg-white/90 font-medium text-xs px-4"
                >
                  {isUpdating ? (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Download className="w-3 h-3 mr-1" />
                      Update Now
                    </>
                  )}
                </Button>
                
                <span className="text-white/50 text-xs">
                  v{updateInfo.version}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAUpdatePrompt;
